import React, { useState, useEffect, useRef } from 'react';
import AdminSidebar from '../../components/AdminSidebar';
import AdminTopBar from '../../components/AdminTopBar';
import { API_BASE_URL } from '../../config';
import { ADMIN_ROLE_KEYS } from '../../constants/AdminRoleMap';
import RegistrationSurveyFields from '../../components/RegistrationSurveyFields';
import { buildRegistrationIntakeSurveyPayload, normalizeRegistrationSurveyAnswers } from '../../utils/registrationSurveyModel';

const getAdminHeaders = () => {
  const token = localStorage.getItem('adminToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function AdminUsers() {
  const defaultSubscriptionForm = () => ({
    moduleKey: 'global',
    durationPreset: '1',
    startedAt: new Date().toISOString().slice(0, 16),
    expiresAt: '',
    billingType: 'eft',
    paymentReference: '',
    note: ''
  });

  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('Tümü');
  const [dateFilter, setDateFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('Tümü');
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [modalUser, setModalUser] = useState({
    name: '', email: '',
    isPremium: false, premiumExpiresAt: null,
    hasAlakartPremium: false, alakartExpiresAt: null,
    hasAllergenPremium: false, allergenExpiresAt: null,
    hasDietPremium: false, dietExpiresAt: null,
    hasAthletePremium: false, athleteExpiresAt: null,
    hasSpecialDaysPremium: false, specialDaysExpiresAt: null
  });
  const [modalAvatar, setModalAvatar] = useState(null);
  const [modalAvatarDirty, setModalAvatarDirty] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [modalAnswers, setModalAnswers] = useState({ preferences: [], servingSize: 2, frequency: 'Haftada 3-4 Gün', cookingDays: 3 });
  const [modalChildren, setModalChildren] = useState([]);
  const [userIngredientData, setUserIngredientData] = useState({ batches: [], meals: [] });
  const [templates, setTemplates] = useState([]);
  const [currentAdminId, setCurrentAdminId] = useState(null);
  const [activeTab, setActiveTab] = useState('kullanici');
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteType, setDeleteType] = useState('single'); // 'single' | 'bulk'
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [subscriptionData, setSubscriptionData] = useState({ grants: [], entitlements: null });
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [subscriptionError, setSubscriptionError] = useState(null);
  const [subscriptionSuccess, setSubscriptionSuccess] = useState(null);
  const [subscriptionActionLoading, setSubscriptionActionLoading] = useState(false);
  const [subscriptionForm, setSubscriptionForm] = useState(defaultSubscriptionForm());
  const [availableRbacRoles, setAvailableRbacRoles] = useState([]);
  const [selectedRbacRoleKeys, setSelectedRbacRoleKeys] = useState([]);

  // Helper for computing readable age labels
  function computeAgeLabel(birthDate) {
    if (!birthDate) return '';
    const birth = new Date(birthDate);
    if (Number.isNaN(birth.getTime())) return '';
    const now = new Date();
    const diffMs = now - birth;
    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (days < 30) return `${days} günlük`;
    const months = Math.floor(days / 30.44);
    if (months < 12) return `${months} aylık`;
    const years = Math.floor(months / 12);
    const remMonths = months % 12;
    return remMonths > 0 ? `${years} yaş ${remMonths} ay` : `${years} yaş`;
  }

  // Fix Turkish character encoding issues - shared function
  function fixTurkishChars(str) {
    if (!str) return '';
    return str
      .replace(/\u00C3\u00BC/g, 'ü').replace(/\u00C3\u0153/g, 'Ü')
      .replace(/\u00C3\u00B6/g, 'ö').replace(/\u00C3\u2013/g, 'Ö')
      .replace(/\u00C3\u00A7/g, 'ç').replace(/\u00C3\u2021/g, 'Ç')
      .replace(/\u00C4\u00B1/g, 'ı').replace(/\u00C4\u00B0/g, 'İ')
      .replace(/\u00C5\u017E/g, 'ş').replace(/\u00C5\u0178/g, 'ş')
      .replace(/\u00C4\u0178/g, 'ğ');
  }

  function toDateTimeLocalInput(value) {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
  }

  function formatDateTime(value) {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleString('tr-TR');
  }

  function daysRemaining(value) {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return Math.ceil((date.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
  }

  function addMonthsToDate(dateValue, months) {
    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return null;
    const next = new Date(date);
    next.setMonth(next.getMonth() + Number(months || 0));
    return next;
  }

  function applyEntitlementsToUi(userId, entitlements) {
    if (!entitlements?.modules) return;

    const patch = {
      isPremium: !!entitlements.modules.global?.granted,
      premiumExpiresAt: entitlements.modules.global?.expiresAt || null,
      hasAlakartPremium: !!entitlements.modules.alakart?.granted,
      alakartExpiresAt: entitlements.modules.alakart?.expiresAt || null,
      hasAllergenPremium: !!entitlements.modules.allergen?.granted,
      allergenExpiresAt: entitlements.modules.allergen?.expiresAt || null,
      hasDietPremium: !!entitlements.modules.diet?.granted,
      dietExpiresAt: entitlements.modules.diet?.expiresAt || null,
      hasAthletePremium: !!entitlements.modules.athlete?.granted,
      athleteExpiresAt: entitlements.modules.athlete?.expiresAt || null,
      hasSpecialDaysPremium: !!entitlements.modules.special_days?.granted,
      specialDaysExpiresAt: entitlements.modules.special_days?.expiresAt || null
    };

    setModalUser((prev) => ({ ...prev, ...patch }));
    setUsers((prev) => (prev || []).map((item) => (
      item.id === userId ? { ...item, ...patch } : item
    )));
  }

  async function loadSubscriptions(userId) {
    if (!userId) return;
    try {
      setSubscriptionLoading(true);
      setSubscriptionError(null);

      const res = await fetch(`${API_BASE_URL}/api/admin/user/${userId}/subscriptions`, {
        credentials: 'include',
        headers: getAdminHeaders()
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || 'Abonelik bilgileri alınamadı');
      }

      setSubscriptionData({
        grants: Array.isArray(data.grants) ? data.grants : [],
        entitlements: data.entitlements || null
      });
      applyEntitlementsToUi(userId, data.entitlements);
    } catch (err) {
      console.error('load subscriptions error', err);
      setSubscriptionError(err.message || 'Abonelik verisi alınamadı');
      setSubscriptionData({ grants: [], entitlements: null });
    } finally {
      setSubscriptionLoading(false);
    }
  }

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`${API_BASE_URL}/api/admin/users`, { credentials: 'include', headers: getAdminHeaders() });
        if (!mounted) return;
        if (!res.ok) {
          const j = await res.json().catch(() => ({}));
          throw new Error(j.error || 'failed to fetch users');
        }
        const j = await res.json();
        setUsers(Array.isArray(j.users) ? j.users : []);
      } catch (err) {
        console.error('fetch users error', err);
        setError(err.message || 'Error');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    // fetch survey templates for label mapping in modal
    (async () => {
      try {
        const r = await fetch(`${API_BASE_URL}/api/surveys/templates`, { credentials: 'include', headers: getAdminHeaders() });
        if (r && r.ok) {
          const j = await r.json().catch(() => ({}));
          setTemplates(Array.isArray(j.templates) ? j.templates : []);
        }
      } catch (e) { /* ignore */ }
    })();
    // fetch current admin id for localStorage write decisions
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/auth/admin/me`, { credentials: 'include', headers: getAdminHeaders() });
        if (res && res.ok) {
          const j = await res.json().catch(() => null);
          if (j && j.id) setCurrentAdminId(j.id);
        }
      } catch (e) { }
    })();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (!modalOpen || !selectedUser?.id) return;
    loadSubscriptions(selectedUser.id);
  }, [modalOpen, selectedUser?.id]);

  // apply client-side filters
  const filtered = (users || []).filter(u => {
    const q = (search || '').trim().toLowerCase();

    // Check if user itself matches
    const selfMatches = `${u.name || ''} ${u.email || ''} ${String(u.id || '')}`.toLowerCase().includes(q);

    // Check if parent matches (to keep child in search)
    let parentMatches = false;
    if (u.partnerId) {
      const parent = users.find(x => x.id === u.partnerId);
      if (parent) {
        parentMatches = `${parent.name || ''} ${parent.email || ''} ${String(parent.id || '')}`.toLowerCase().includes(q);
      }
    }

    // Check if any child matches (to keep parent in search)
    let childMatches = false;
    const directChildren = users.filter(x => x.partnerId === u.id);
    if (directChildren.length > 0) {
      childMatches = directChildren.some(c => `${c.name || ''} ${c.email || ''} ${String(c.id || '')}`.toLowerCase().includes(q));
    }

    if (q && !(selfMatches || parentMatches || childMatches)) return false;

    // type filter (use partnerId instead of level for pre-hierarchy filtering)
    if (typeFilter && typeFilter !== 'Tümü') {
      if (typeFilter === 'Admin' && !u.isAdmin) return false;
      if (typeFilter === 'Normal Üye' && (u.isAdmin || !!u.partnerId)) return false;
      if (typeFilter === 'Partner Üye' && !u.partnerId) return false;
      if (typeFilter === 'Premium Üye' && (!u.isPremium || u.isAdmin)) return false;
    }

    // date filter (YYYY-MM-DD) compare createdAt date
    if (dateFilter) {
      const created = u.createdAt ? new Date(u.createdAt).toISOString().slice(0, 10) : '';
      if (created !== dateFilter) return false;
    }

    // status filter (assume u.status or default 'Aktif')
    if (statusFilter && statusFilter !== 'Tümü') {
      const s = (u.status || 'Aktif') || '';
      if (s !== statusFilter) return false;
    }

    return true;
  });

  const totalPages = Math.max(1, Math.ceil((filtered?.length || 0) / pageSize));

  // Flatten hierarchical data for paged display if needed, or group them
  const processHierarchy = (flatUsers) => {
    const userMap = {};
    flatUsers.forEach(u => { userMap[u.id] = { ...u, children: [] }; });

    const processedAsChild = new Set();
    const roots = [];

    // First pass: Link children to parents while avoiding circular loops
    flatUsers.forEach(u => {
      const mapped = userMap[u.id];
      if (u.partnerId && userMap[u.partnerId]) {
        const partner = userMap[u.partnerId];
        const isMutual = partner.partnerId === u.id;

        // In a mutual relationship, only the one with the LARGER ID becomes a child of the smaller ID
        // This breaks the loop and keeps the hierarchy stable.
        if (isMutual) {
          if (u.id > partner.id) {
            userMap[u.partnerId].children.push(mapped);
            processedAsChild.add(u.id);
          }
        } else {
          userMap[u.partnerId].children.push(mapped);
          processedAsChild.add(u.id);
        }
      }
    });

    // Second pass: Identify roots (those not processed as children)
    flatUsers.forEach(u => {
      if (!processedAsChild.has(u.id)) {
        roots.push(userMap[u.id]);
      }
    });

    // Flatten back to list but with level info for rendering
    const flattened = [];
    const walk = (list, level = 0) => {
      list.forEach(node => {
        flattened.push({ ...node, level });
        if (node.children.length > 0) walk(node.children, level + 1);
      });
    };
    walk(roots);
    return flattened;
  };

  const paged = processHierarchy(filtered).slice((page - 1) * pageSize, page * pageSize);

  function openEdit(u) {
    setSelectedUser(u);
    setModalUser({
      name: u.name || '',
      email: u.email || '',
      isPremium: u.isPremium || false,
      premiumExpiresAt: u.premiumExpiresAt || null,
      hasAlakartPremium: u.hasAlakartPremium || false,
      alakartExpiresAt: u.alakartExpiresAt || null,
      hasAllergenPremium: u.hasAllergenPremium || false,
      allergenExpiresAt: u.allergenExpiresAt || null,
      hasDietPremium: u.hasDietPremium || false,
      dietExpiresAt: u.dietExpiresAt || null,
      hasAthletePremium: u.hasAthletePremium || false,
      athleteExpiresAt: u.athleteExpiresAt || null,
      hasSpecialDaysPremium: u.hasSpecialDaysPremium || false,
      specialDaysExpiresAt: u.specialDaysExpiresAt || null,
      isAdmin: u.isAdmin || false
    });
    setSelectedRbacRoleKeys([]);
    setAvailableRbacRoles([]);
    setModalAvatar(u.avatar || null);
    setActiveTab('kullanici'); // Reset to first tab when opening modal
    setSubscriptionData({ grants: [], entitlements: null });
    setSubscriptionError(null);
    setSubscriptionSuccess(null);
    setSubscriptionForm(defaultSubscriptionForm());

    // fetch profile/survey for this user via admin endpoint — bypass cache
    (async () => {
      setModalAnswers({ preferences: [], servingSize: 2, frequency: 'Haftada 3-4 Gün', cookingDays: 3 });
      setUserIngredientData({ batches: [], meals: [] });

      try {
        // Fetch user profile
        const res = await fetch(`${API_BASE_URL}/api/admin/user/${u.id}/profile`, {
          credentials: 'include',
          cache: 'no-store',
          headers: {
            'Accept': 'application/json; charset=utf-8',
            'Content-Type': 'application/json; charset=utf-8',
            ...getAdminHeaders()
          }
        });
        if (res.ok) {
          const j = await res.json();
          const profile = j.profile || null;
          const survey = j.survey || null;
          const rbac = j.rbac || null;
          const intake = (profile && profile.intakeSurvey) || (profile && profile.answers) || (survey && survey.answers) || profile || {};
          const answers = intake || survey || {};
          setModalAvatar((profile && profile.avatar) || (answers && answers.avatar) || u.avatar || null);

          // merge top-level intake keys and intake.answers into a flat object for modal display
          const answersObj = (intake && intake.answers) ? intake.answers : intake;
          const flat = Object.assign({}, (answersObj && typeof answersObj === 'object' ? answersObj : {}));

          // copy top-level non-answers fields (excluding avatar)
          if (intake && typeof intake === 'object') {
            for (const [k, v] of Object.entries(intake)) {
              if (k === 'answers' || k === 'avatar') continue;
              if (flat[k] === undefined) flat[k] = v;
            }
          }

          // Add profile data (including baby info) for easy access in tabs
          if (profile) {
            flat.profile = profile;

            // Set children state
            if (profile.children) {
              try {
                const childrenData = typeof profile.children === 'string' ? JSON.parse(profile.children) : profile.children;
                setModalChildren(Array.isArray(childrenData) ? childrenData : []);
              } catch (e) {
                console.warn('Failed to parse children data in admin', e);
                setModalChildren([]);
              }
            } else {
              setModalChildren([]);
            }
          } else {
            setModalChildren([]);
          }

          setModalAnswers({
            ...normalizeRegistrationSurveyAnswers(intake || flat || {}),
            profile: profile || null
          });
          setAvailableRbacRoles(Array.isArray(rbac?.availableRoles) ? rbac.availableRoles : []);
          setSelectedRbacRoleKeys(Array.isArray(rbac?.roleKeys) ? rbac.roleKeys : []);
        }

        // Fetch user ingredient data (inventory batches and meal history)
        let userBatches = [];
        let userMeals = [];

        try {
          const [batchesRes, mealsRes] = await Promise.all([
            fetch(`${API_BASE_URL}/api/admin/inventory-batches`, { credentials: 'include', headers: getAdminHeaders() }).catch(() => null),
            fetch(`${API_BASE_URL}/api/admin/meal-history`, { credentials: 'include', headers: getAdminHeaders() }).catch(() => null)
          ]);

          if (batchesRes && batchesRes.ok) {
            const batchData = await batchesRes.json();
            userBatches = (batchData.batches || []).filter(batch =>
              batch.user_id === u.id.toString() || batch.user_id === u.id
            );
          } else {
            // Fallback static data for demo
            userBatches = [
              {
                id: 1,
                ingredient_name: 'Domates',
                remaining_amount: 150,
                initial_amount: 300,
                unit: 'gr',
                expiry_date: '2025-12-28',
                is_opened: true,
                is_frozen: false,
                storage_location: 'buzdolabı'
              },
              {
                id: 2,
                ingredient_name: 'Soğan',
                remaining_amount: 100,
                initial_amount: 200,
                unit: 'gr',
                expiry_date: '2025-12-30',
                is_opened: false,
                is_frozen: false,
                storage_location: 'kiler'
              }
            ];
          }

          if (mealsRes && mealsRes.ok) {
            const mealData = await mealsRes.json();
            userMeals = (mealData.meals || []).filter(meal =>
              meal.user_id === u.id.toString() || meal.user_id === u.id
            );
          } else {
            // Fallback static data for demo
            userMeals = [
              {
                id: 1,
                recipe_title: 'Domates Soslu Pilav',
                used_ingredients: '{"1": {"amount": 100, "unit": "gr"}, "3": {"amount": 150, "unit": "gr"}}',
                cooked_at: '2025-12-22T18:30:00Z'
              }
            ];
          }
        } catch (err) {
          console.error('Error fetching ingredient data:', err);
          // Use fallback data
          userBatches = [];
          userMeals = [];
        }

        setUserIngredientData({ batches: userBatches, meals: userMeals });

      } catch (err) {
        console.error('fetch admin user data', err);
        setModalAnswers({ preferences: [], servingSize: 2, frequency: 'Haftada 3-4 Gün', cookingDays: 3 });
        setUserIngredientData({ batches: [], meals: [] });
        setAvailableRbacRoles([]);
        setSelectedRbacRoleKeys([]);
      }
    })();
    setModalOpen(true);
  }

  async function handleCreateSubscription() {
    if (!selectedUser) return;

    try {
      setSubscriptionActionLoading(true);
      setSubscriptionError(null);
      setSubscriptionSuccess(null);

      const startValue = subscriptionForm.startedAt ? new Date(subscriptionForm.startedAt) : new Date();
      if (Number.isNaN(startValue.getTime())) {
        throw new Error('Başlangıç tarihi geçersiz');
      }

      let resolvedExpiresAt = null;
      if (subscriptionForm.durationPreset === 'custom') {
        resolvedExpiresAt = subscriptionForm.expiresAt ? new Date(subscriptionForm.expiresAt) : null;
        if (!resolvedExpiresAt || Number.isNaN(resolvedExpiresAt.getTime())) {
          throw new Error('Bitiş tarihi seçilmelidir');
        }
      } else {
        resolvedExpiresAt = addMonthsToDate(startValue, Number(subscriptionForm.durationPreset));
      }

      const res = await fetch(`${API_BASE_URL}/api/admin/user/${selectedUser.id}/subscriptions`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...getAdminHeaders() },
        body: JSON.stringify({
          moduleKey: subscriptionForm.moduleKey,
          startedAt: startValue.toISOString(),
          expiresAt: resolvedExpiresAt?.toISOString(),
          months: subscriptionForm.durationPreset === 'custom' ? null : Number(subscriptionForm.durationPreset),
          billingType: subscriptionForm.billingType,
          paymentReference: subscriptionForm.paymentReference || null,
          note: subscriptionForm.note || null,
          status: 'active'
        })
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || 'Abonelik oluşturulamadı');
      }

      setSubscriptionForm(defaultSubscriptionForm());
      setSubscriptionSuccess('Abonelik tanımı kaydedildi.');
      await loadSubscriptions(selectedUser.id);
    } catch (err) {
      console.error('create subscription error', err);
      setSubscriptionError(err.message || 'Abonelik oluşturulamadı');
    } finally {
      setSubscriptionActionLoading(false);
    }
  }

  async function handleCancelSubscription(grantId) {
    if (!selectedUser || !grantId) return;
    try {
      setSubscriptionActionLoading(true);
      setSubscriptionError(null);
      setSubscriptionSuccess(null);

      const res = await fetch(`${API_BASE_URL}/api/admin/subscriptions/${grantId}/cancel`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...getAdminHeaders() }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || 'Abonelik iptal edilemedi');
      }

      setSubscriptionSuccess('Abonelik iptal edildi.');
      await loadSubscriptions(selectedUser.id);
    } catch (err) {
      console.error('cancel subscription error', err);
      setSubscriptionError(err.message || 'Abonelik iptal edilemedi');
    } finally {
      setSubscriptionActionLoading(false);
    }
  }

  const fileInputRef = useRef(null);

  function onPhotoButtonClick() {
    if (!selectedUser) return;
    fileInputRef.current && fileInputRef.current.click();
  }

  async function handleFileChange(e) {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      setModalAvatar(dataUrl);
      setModalAvatarDirty(true);
      try {
        // Do not write admin avatar to global frontend localStorage here.
        // Admin avatars must not overwrite frontend user cache.
      } catch (e) { }
    };
    reader.readAsDataURL(f);
    // reset input so same file can be chosen again if needed
    e.target.value = '';
  }

  // Save changes (profile) when user clicks the save button
  async function saveProfileChanges() {
    if (!selectedUser) return;
    if (modalUser.isAdmin && selectedRbacRoleKeys.length === 0) {
      setSaveError('Admin kullanıcı için en az bir rol seçilmelidir.');
      setSaveSuccess(false);
      return;
    }
    try {
      const { profile, avatar: existingAvatar, answers: nestedAnswers, ...rest } = modalAnswers || {};
      const surveyAnswers = normalizeRegistrationSurveyAnswers(nestedAnswers || rest || {});
      const intake = buildRegistrationIntakeSurveyPayload(surveyAnswers, {
        completedRegistration: profile?.intakeSurvey?.completedRegistration
      });
      const avatarToSave = modalAvatarDirty ? modalAvatar : (selectedUser.avatar || existingAvatar);
      if (avatarToSave) intake.avatar = avatarToSave;

      // Ensure Profile table specific fields are preserved and sent
      const profileUpdates = {
        babyName: modalAnswers.babyName || (modalAnswers.profile && modalAnswers.profile.babyName),
        babyBirthDate: modalAnswers.babyBirthDate || (modalAnswers.profile && modalAnswers.profile.babyBirthDate),
        hasBaby: modalAnswers.hasBaby !== undefined ? modalAnswers.hasBaby : (modalAnswers.profile && modalAnswers.profile.hasBaby),
        servingSize: Number.parseInt(surveyAnswers.q3 || modalAnswers.servingSize || (modalAnswers.profile && modalAnswers.profile.servingSize), 10) || undefined,
        children: modalChildren
      };

      const res = await fetch(`${API_BASE_URL}/api/admin/user/${selectedUser.id}/profile`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...getAdminHeaders() },
        body: JSON.stringify({
          intakeSurvey: intake,
          user: {
            name: modalUser.name,
            email: modalUser.email,
            phone: modalUser.phone,
            isAdmin: modalUser.isAdmin,
          },
          rbacRoleKeys: modalUser.isAdmin ? selectedRbacRoleKeys : [],
          profile: profileUpdates,
          children: modalChildren
        }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        const msg = j && j.error ? j.error : 'Kaydetme başarısız';
        console.error('save profile failed', res.status, j);
        setSaveError(msg);
        setSaveSuccess(false);
        return;
      }
      const j = await res.json().catch(() => ({}));
      // if server returned updated user/profile, merge into UI
      if (j && j.user) {
        setUsers(prev => (prev || []).map(x => (x.id === selectedUser.id ? {
          ...x,
          name: j.user.name,
          email: j.user.email,
          isAdmin: j.user.isAdmin,
          isPremium: j.user.isPremium,
          hasAlakartPremium: j.user.hasAlakartPremium,
          hasAllergenPremium: j.user.hasAllergenPremium,
          hasDietPremium: j.user.hasDietPremium,
          hasAthletePremium: j.user.hasAthletePremium,
          hasSpecialDaysPremium: j.user.hasSpecialDaysPremium,
          avatar: j.profile && j.profile.intakeSurvey && j.profile.intakeSurvey.avatar ? j.profile.intakeSurvey.avatar : x.avatar
        } : x)));
        setModalUser(m => ({
          ...m,
          name: j.user.name,
          email: j.user.email,
          isAdmin: j.user.isAdmin,
          isPremium: j.user.isPremium,
          hasAlakartPremium: j.user.hasAlakartPremium,
          hasAllergenPremium: j.user.hasAllergenPremium,
          hasDietPremium: j.user.hasDietPremium,
          hasAthletePremium: j.user.hasAthletePremium,
          hasSpecialDaysPremium: j.user.hasSpecialDaysPremium
        }));
        setAvailableRbacRoles(Array.isArray(j?.rbac?.availableRoles) ? j.rbac.availableRoles : []);
        setSelectedRbacRoleKeys(Array.isArray(j?.rbac?.roleKeys) ? j.rbac.roleKeys : []);
      } else if (intake.avatar) {
        setUsers(prev => (prev || []).map(x => (x.id === selectedUser.id ? { ...x, avatar: intake.avatar } : x)));
      }
      // reflect changes in UI: update users list avatar if provided
      if (intake.avatar) {
        setUsers(prev => (prev || []).map(x => (x.id === selectedUser.id ? { ...x, avatar: intake.avatar } : x)));
      }
      // reset dirty flag
      setModalAvatarDirty(false);
      // show success message briefly
      setSaveError(null);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 4000);
      // notify other components (AdminSidebar) about profile change
      try {
        const detail = { userId: selectedUser.id, avatar: intake.avatar || null, name: modalUser.name || null, email: modalUser.email || null };
        // prefer server-returned values if available
        if (j && j.user) {
          detail.name = j.user.name || detail.name;
          detail.email = j.user.email || detail.email;
        }
        window.dispatchEvent(new CustomEvent('adminProfileUpdated', { detail }));
      } catch (e) {
        // ignore if CustomEvent not supported
      }
      // Do NOT persist admin avatar into the global frontend localStorage here.
      // Admin-side updates must not modify frontend user avatar cache.
    } catch (err) {
      console.error('saveProfileChanges error', err);
      setSaveError('Kaydetme sırasında hata oluştu');
      setSaveSuccess(false);
    }
  }

  function closeModal() {
    setModalOpen(false);
    setSelectedUser(null);
    setModalUser({ name: '', email: '' });
    setModalAvatar(null);
    setModalAnswers({ preferences: [], servingSize: 2, frequency: 'Haftada 3-4 Gün', cookingDays: 3 });
    setSubscriptionData({ grants: [], entitlements: null });
    setSubscriptionError(null);
    setSubscriptionSuccess(null);
    setSubscriptionForm(defaultSubscriptionForm());
    setAvailableRbacRoles([]);
    setSelectedRbacRoleKeys([]);
  }

  async function handleDeleteConfirm() {
    setDeleteError(null);
    setDeleteLoading(true);
    try {
      const url = deleteType === 'single'
        ? `${API_BASE_URL}/api/admin/user/${userToDelete.id}/delete`
        : `${API_BASE_URL}/api/admin/users/bulk-delete`;

      const body = deleteType === 'single'
        ? { password: deletePassword }
        : { userIds: selectedUserIds, password: deletePassword };

      const res = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...getAdminHeaders() },
        body: JSON.stringify(body)
      });

      const j = await res.json();
      if (!res.ok) throw new Error(j.error || 'Silme işlemi başarısız');

      // Refresh users list
      setUsers(prev => prev.filter(u => {
        if (deleteType === 'single') return u.id !== userToDelete.id;
        return !selectedUserIds.includes(u.id);
      }));

      setDeleteModalOpen(false);
      setDeletePassword('');
      setSelectedUserIds([]);

      // Toast notification if available, or just alert
      alert('Başarıyla silindi');
    } catch (err) {
      setDeleteError(err.message);
    } finally {
      setDeleteLoading(false);
    }
  }

  function openDeleteModal(type, target = null) {
    setDeleteType(type);
    setUserToDelete(target);
    setDeletePassword('');
    setDeleteError(null);
    setDeleteModalOpen(true);
  }

  return (
    <div className="flex h-screen w-full bg-slate-50 dark:bg-[#0d141c]">
      <AdminSidebar mobileOpen={mobileSidebarOpen} onCloseMobile={() => setMobileSidebarOpen(false)} />
      <main className="flex-1 flex flex-col h-full overflow-hidden bg-background-light dark:bg-[#0d141c] relative md:ml-[var(--admin-sidebar-width,18rem)] transition-[margin] duration-300">
        <AdminTopBar onToggleMobile={() => setMobileSidebarOpen(open => !open)} />

        <div className="flex-1 overflow-y-auto p-6 lg:p-10">
          <div className="flex flex-col gap-8 max-w-[1440px] mx-auto w-full">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <h1 className="text-2xl font-bold text-[#181111] dark:text-white tracking-tight">Kullanıcı Yönetimi</h1>
                <p className="text-gray-500 dark:text-slate-400 text-sm mt-1">Sistemdeki kayıtlı kullanıcıları görüntüleyin ve yönetin.</p>
              </div>
              <div className="flex gap-3">
                {selectedUserIds.length > 0 && (
                  <button
                    onClick={() => openDeleteModal('bulk')}
                    className="flex items-center gap-2 px-4 py-2.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-900/50 rounded-lg text-sm font-medium hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors shadow-sm"
                  >
                    <span className="material-symbols-outlined text-[20px]">delete_sweep</span>
                    Seçilenleri Sil ({selectedUserIds.length})
                  </button>
                )}
                <button className="flex items-center gap-2 px-4 py-2.5 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg text-sm font-medium hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors shadow-sm text-gray-700 dark:text-slate-200">
                  <span className="material-symbols-outlined text-[20px]">file_download</span>
                  Dışa Aktar
                </button>
                <button className="flex items-center gap-2 px-4 py-2.5 bg-primary hover:bg-primary-hover text-white rounded-lg text-sm font-medium transition-colors shadow-sm">
                  <span className="material-symbols-outlined text-[20px]">add</span>
                  Yeni Kullanıcı Ekle
                </button>
              </div>
            </div>

            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 p-5">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-4 items-end">
                <div className="lg:col-span-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">Arama</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="material-symbols-outlined text-gray-400 text-[20px]">search</span>
                    </div>
                    <input
                      className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 dark:border-slate-600 rounded-lg leading-5 bg-white dark:bg-slate-900 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary sm:text-sm"
                      placeholder="Ad, e-posta veya ID ara..."
                      type="text"
                      value={search}
                      onChange={e => { setSearch(e.target.value); setPage(1); }}
                    />
                  </div>


                </div>
                <div className="lg:col-span-3">
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">Kullanıcı Tipi</label>
                  <select
                    className="block w-full pl-3 pr-10 py-2.5 text-base border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-gray-900 dark:text-white focus:outline-none focus:ring-primary focus:border-primary sm:text-sm rounded-lg"
                    value={typeFilter}
                    onChange={e => { setTypeFilter(e.target.value); setPage(1); }}
                  >
                    <option>Tümü</option>
                    <option>Normal Üye</option>
                    <option>Partner Üye</option>
                    <option>Premium Üye</option>
                    <option>Admin</option>
                  </select>
                </div>
                <div className="lg:col-span-3">
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">Kayıt Tarihi</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="material-symbols-outlined text-gray-400 text-[20px]">calendar_today</span>
                    </div>
                    <input
                      className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 dark:border-slate-600 rounded-lg leading-5 bg-white dark:bg-slate-900 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary sm:text-sm"
                      type="date"
                      value={dateFilter}
                      onChange={e => { setDateFilter(e.target.value); setPage(1); }}
                    />
                  </div>
                </div>
                <div className="lg:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">Durum</label>
                  <select
                    className="block w-full pl-3 pr-10 py-2.5 text-base border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-gray-900 dark:text-white focus:outline-none focus:ring-primary focus:border-primary sm:text-sm rounded-lg"
                    value={statusFilter}
                    onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
                  >
                    <option>Tümü</option>
                    <option>Aktif</option>
                    <option>Pasif</option>
                    <option>Beklemede</option>
                    <option>Yasaklı</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 overflow-hidden flex flex-col flex-1">
              {loading ? (
                <div className="p-6 text-gray-900 dark:text-slate-300">Yükleniyor...</div>
              ) : error ? (
                <div className="p-6 text-red-600">Hata: {error}</div>
              ) : (
                <>
                  {/* Desktop Table */}
                  <div className="hidden md:block overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-slate-700">
                      <thead className="bg-gray-50 dark:bg-slate-900/50">
                        <tr>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider w-16" scope="col">
                            <input
                              className="rounded border-gray-300 dark:border-slate-600 text-primary focus:ring-primary h-4 w-4 bg-white dark:bg-slate-900"
                              type="checkbox"
                              checked={selectedUserIds.length === paged.length && paged.length > 0}
                              onChange={(e) => {
                                if (e.target.checked) setSelectedUserIds(paged.map(ux => ux.id));
                                else setSelectedUserIds([]);
                              }}
                            />
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider" scope="col">Kullanıcı</th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider" scope="col">Kayıt Tarihi</th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider" scope="col">Son Giriş Tarihi</th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider" scope="col">Kullanıcı Tipi</th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider" scope="col">Durum</th>
                          <th className="px-6 py-4 text-right text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider" scope="col">İşlemler</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-slate-800 divide-y divide-gray-200 dark:divide-slate-700">
                        {paged.map(u => (
                          <tr key={u.id} className={`${(u.level || 0) > 0 ? 'bg-gray-50/50 dark:bg-slate-900/20' : ''} hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors group`}>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <input
                                className="rounded border-gray-300 dark:border-slate-600 text-primary focus:ring-primary h-4 w-4 bg-white dark:bg-slate-900"
                                type="checkbox"
                                checked={selectedUserIds.includes(u.id)}
                                onChange={(e) => {
                                  if (e.target.checked) setSelectedUserIds([...selectedUserIds, u.id]);
                                  else setSelectedUserIds(selectedUserIds.filter(id => id !== u.id));
                                }}
                              />
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center" style={{ marginLeft: `${(u.level || 0) * 32}px` }}>
                                {(u.level || 0) > 0 && (
                                  <span className="material-symbols-outlined text-gray-400 mr-2 text-[20px] font-bold">subdirectory_arrow_right</span>
                                )}
                                <div className="h-10 w-10 flex-shrink-0">
                                  <div className="h-10 w-10 rounded-full bg-cover bg-center ring-2 ring-white dark:ring-slate-700 shadow-sm" style={{ backgroundImage: `url(${u.avatar || 'https://www.gravatar.com/avatar/?d=mp'})` }} />
                                </div>
                                <div className="ml-4">
                                  <div className="text-sm font-bold text-gray-900 dark:text-white flex items-center gap-2 flex-wrap">
                                    {u.name || u.email}
                                    {(u.level || 0) > 0 && (
                                      <span className="text-[10px] bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 px-1.5 py-0.5 rounded leading-none font-medium flex items-center gap-1">
                                        Partner
                                        {u.partnerId && users.find(x => x.id === u.partnerId) && (
                                          <span className="opacity-70 font-normal">
                                            : {users.find(x => x.id === u.partnerId)?.name || 'Ana Hesap'}
                                          </span>
                                        )}
                                      </span>
                                    )}
                                  </div>
                                  <div className="text-xs text-gray-500 dark:text-slate-400">{u.email}</div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap"><div className="text-sm text-gray-900 dark:text-slate-300 font-medium">{u.createdAt ? new Date(u.createdAt).toLocaleDateString('tr-TR') : '-'}</div></td>
                            <td className="px-6 py-4 whitespace-nowrap"><div className="text-sm text-gray-900 dark:text-slate-300 font-medium">{u.lastActiveAt ? new Date(u.lastActiveAt).toLocaleDateString('tr-TR') : '-'}</div></td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`px-3 py-1 inline-flex text-xs leading-5 font-bold rounded-full ${u.isAdmin ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' : ((u.level || 0) > 0 ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' : (u.isPremium ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400' : 'bg-gray-100 text-gray-800 dark:bg-slate-700 dark:text-slate-300'))}`}>
                                {u.isAdmin ? 'Admin' : ((u.level || 0) > 0 ? 'Partner Üye' : (u.isPremium ? 'Premium Üye' : 'Normal Üye'))}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-md text-sm font-bold bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400"><span className="w-1.5 h-1.5 rounded-full bg-blue-600 dark:bg-blue-400"></span>Aktif</span>
                                <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-md text-xs font-bold ${u.hasPushEnabled ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400' : 'bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-300'}`}>
                                  <span className={`w-1.5 h-1.5 rounded-full ${u.hasPushEnabled ? 'bg-emerald-500' : 'bg-slate-400'}`}></span>
                                  {u.hasPushEnabled ? 'Push Açık' : 'Push Kapalı'}
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <div className="flex justify-end gap-2">
                                <button onClick={() => openEdit(u)} className="cursor-pointer text-gray-400 dark:text-slate-500 hover:text-primary dark:hover:text-primary transition-colors p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-full inline-flex items-center">
                                  <span className="material-symbols-outlined text-[20px]">edit</span>
                                </button>
                                {!u.isAdmin && (
                                  <button onClick={() => openDeleteModal('single', u)} className="cursor-pointer text-gray-400 dark:text-slate-500 hover:text-red-500 dark:hover:text-red-400 transition-colors p-2 hover:bg-red-50 dark:hover:bg-red-900/10 rounded-full inline-flex items-center">
                                    <span className="material-symbols-outlined text-[20px]">delete</span>
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile Cards */}
                  <div className="md:hidden divide-y divide-gray-200 dark:divide-slate-700">
                    {paged.map(u => (
                      <div key={u.id} className="p-4 hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors">
                        {/* User Info */}
                        <div className="flex items-start gap-3 mb-3">
                          <div className="h-12 w-12 flex-shrink-0">
                            <div className="h-12 w-12 rounded-full bg-cover bg-center ring-2 ring-gray-200 dark:ring-slate-600" style={{ backgroundImage: `url(${u.avatar || 'https://www.gravatar.com/avatar/?d=mp'})` }} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-gray-900 dark:text-white truncate">{u.name || u.email}</div>
                            <div className="text-xs text-gray-500 dark:text-slate-400 truncate">{u.email}</div>
                            <div className="text-xs text-gray-400 dark:text-slate-500 mt-0.5">ID: #{u.id}</div>
                          </div>
                        </div>

                        {/* Badges */}
                        <div className="flex flex-wrap items-center gap-2 mb-3">
                          <span className={`px-2.5 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${u.isAdmin ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' : 'bg-gray-100 text-gray-800 dark:bg-slate-700 dark:text-slate-300'}`}>
                            {u.isAdmin ? 'Admin' : 'Kullanıcı'}
                          </span>
                          {u.isPremium && (
                            <span className="px-2.5 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400">
                              Premium
                            </span>
                          )}
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400">
                            <span className="w-1.5 h-1.5 rounded-full bg-blue-600 dark:bg-blue-400"></span>
                            Aktif
                          </span>
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium ${u.hasPushEnabled ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400' : 'bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-300'}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${u.hasPushEnabled ? 'bg-emerald-500' : 'bg-slate-400'}`}></span>
                            {u.hasPushEnabled ? 'Push Açık' : 'Push Kapalı'}
                          </span>
                        </div>

                        {/* Dates & Actions */}
                        <div className="flex items-center justify-between text-xs">
                          <div className="text-gray-500 dark:text-slate-400 flex flex-col gap-0.5">
                            <div><span className="font-medium">Kayıt:</span> {u.createdAt ? new Date(u.createdAt).toLocaleDateString('tr-TR') : '-'}</div>
                            <div><span className="font-medium">Giriş:</span> {u.lastActiveAt ? new Date(u.lastActiveAt).toLocaleDateString('tr-TR') : '-'}</div>
                          </div>
                          <button
                            onClick={() => openEdit(u)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600 text-gray-700 dark:text-slate-300 rounded-lg transition-colors"
                          >
                            <span className="material-symbols-outlined text-[16px]">edit</span>
                            <span className="text-xs font-medium">Düzenle</span>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              <div className="bg-white dark:bg-slate-800 px-4 py-3 flex items-center justify-between border-t border-gray-200 dark:border-slate-700 sm:px-6 mt-auto">
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700 dark:text-slate-400">Toplam <span className="font-medium text-gray-900 dark:text-white">{users.length}</span> sonuç</p>
                  </div>
                  <div>
                    <nav aria-label="Pagination" className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                      <button onClick={() => setPage(p => Math.max(1, p - 1))} className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm font-medium text-gray-500 dark:text-slate-400 hover:bg-gray-50 dark:hover:bg-slate-700">
                        <span className="sr-only">Önceki</span>
                        <span className="material-symbols-outlined text-[20px]">chevron_left</span>
                      </button>
                      {Array.from({ length: totalPages }).map((_, i) => {
                        const num = i + 1;
                        return (
                          <button key={num} onClick={() => setPage(num)} aria-current={page === num} className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${page === num ? 'z-10 bg-primary/10 border-primary text-primary' : 'bg-white dark:bg-slate-800 border-gray-300 dark:border-slate-600 text-gray-500 dark:text-slate-400 hover:bg-gray-50 dark:hover:bg-slate-700'}`}>
                            {num}
                          </button>
                        );
                      })}
                      <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm font-medium text-gray-500 dark:text-slate-400 hover:bg-gray-50 dark:hover:bg-slate-700">
                        <span className="sr-only">Sonraki</span>
                        <span className="material-symbols-outlined text-[20px]">chevron_right</span>
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Modal controlled by state */}
          {modalOpen && selectedUser && (
            <div className="fixed inset-0 z-[100] flex items-center justify-center bg-gray-900/60 dark:bg-slate-900/80 backdrop-blur-sm p-4 sm:p-6 overflow-y-auto animate-in fade-in duration-200">
              <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-3xl my-auto relative flex flex-col max-h-[95vh] border dark:border-slate-700">
                <div className="px-6 py-4 border-b border-gray-100 dark:border-slate-700 flex items-center justify-between sticky top-0 bg-white dark:bg-slate-800 z-10 rounded-t-2xl">
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white">Kullanıcı Bilgilerini Düzenle</h3>
                    <p className="text-sm text-gray-500 dark:text-slate-400 mt-0.5">Kullanıcı profilini ve tercihlerini güncelleyin.</p>
                  </div>
                  <button onClick={() => closeModal()} className="p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-full cursor-pointer transition-colors text-gray-500 dark:text-slate-400">
                    <span className="material-symbols-outlined text-[24px]">close</span>
                  </button>
                </div>
                <div className="p-6 overflow-y-auto custom-scrollbar">
                  <div className="flex flex-col sm:flex-row gap-6 mb-8 items-start sm:items-center p-4 bg-gray-50 dark:bg-slate-900/50 rounded-xl border border-gray-100 dark:border-slate-700">
                    <div className="relative">
                      <div className="h-20 w-20 rounded-full bg-cover bg-center ring-4 ring-white dark:ring-slate-700 shadow-sm" style={{ backgroundImage: `url(${modalAvatar || selectedUser.avatar || 'https://www.gravatar.com/avatar/?d=mp'})` }} />
                      <button onClick={onPhotoButtonClick} className="absolute bottom-0 right-0 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 rounded-full p-1.5 text-gray-600 dark:text-slate-300 hover:text-primary dark:hover:text-primary shadow-sm hover:border-primary dark:hover:border-primary transition-colors">
                        <span className="material-symbols-outlined text-[16px] block">photo_camera</span>
                      </button>
                      <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileChange} />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-xl font-bold text-gray-900 dark:text-white">{modalUser.name || modalUser.email}</h4>
                      <div className="flex items-center gap-2 mt-1 text-sm text-gray-500 dark:text-slate-400">
                        <span className="inline-flex items-center gap-1"><span className="material-symbols-outlined text-[16px]">mail</span> {modalUser.email}</span>
                        <span className="h-1 w-1 rounded-full bg-gray-300 dark:bg-slate-600"></span>
                        <span className="text-xs bg-gray-200 dark:bg-slate-700 px-2 py-0.5 rounded text-gray-700 dark:text-slate-300 font-medium">ID: #{selectedUser.id}</span>
                      </div>
                    </div>
                    <div className="flex flex-col gap-2 w-full sm:w-auto">
                      <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400 justify-center">{selectedUser.isAdmin ? 'Admin' : 'Kullanıcı'}</span>
                      <span className="inline-flex items-center justify-center gap-1.5 px-2.5 py-0.5 rounded-md text-sm font-medium bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400"><span className="w-1.5 h-1.5 rounded-full bg-blue-600 dark:bg-blue-400"></span>Aktif</span>
                    </div>
                  </div>

                  {/* Tab Navigation */}
                  <div className="border-b border-gray-200 dark:border-slate-700 mb-6">
                    <nav className="flex flex-wrap gap-x-4 gap-y-2" aria-label="Tabs">
                      {[
                        { key: 'kullanici', label: 'Kullanıcı', icon: 'person' },
                        { key: 'profil', label: 'Profil', icon: 'account_circle' },
                        { key: 'bebek', label: 'Aile Bilgileri', icon: 'child_care' },
                        { key: 'anket', label: 'Anket', icon: 'quiz' },
                        { key: 'kiler', label: 'Kiler', icon: 'inventory_2' }
                      ].map(tab => (
                        <button
                          key={tab.key}
                          onClick={() => setActiveTab(tab.key)}
                          className={`${activeTab === tab.key
                            ? 'border-primary text-primary bg-primary/5 dark:bg-primary/10'
                            : 'border-transparent text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-200 hover:border-gray-300 dark:hover:border-slate-500'
                            } whitespace-nowrap py-2 px-3 border-b-2 font-medium text-sm flex items-center gap-1.5 rounded-t-lg transition-colors`}
                        >
                          <span className="material-symbols-outlined text-[16px]">{tab.icon}</span>
                          <span className="hidden sm:inline">{tab.label}</span>
                        </button>
                      ))}
                      <button
                        onClick={() => setActiveTab('abonelikler')}
                        className={`${activeTab === 'abonelikler'
                          ? 'border-primary text-primary bg-primary/5 dark:bg-primary/10'
                          : 'border-transparent text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-200 hover:border-gray-300 dark:hover:border-slate-500'
                          } whitespace-nowrap py-2 px-3 border-b-2 font-medium text-sm flex items-center gap-1.5 rounded-t-lg transition-colors`}
                      >
                        <span className="material-symbols-outlined text-[16px]">workspace_premium</span>
                        <span className="hidden sm:inline">Abonelikler</span>
                      </button>
                    </nav>
                  </div>

                  {/* Tab Content */}
                  {activeTab === 'kullanici' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-4">
                        <h5 className="text-sm font-semibold text-gray-900 dark:text-white border-b dark:border-slate-700 pb-2 mb-2">Kişisel Bilgiler</h5>
                        <div className="space-y-1.5">
                          <label className="text-sm font-medium text-gray-700 dark:text-slate-300">Ad Soyad</label>
                          <input className="block w-full rounded-lg border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-gray-900 dark:text-white shadow-sm focus:border-primary focus:ring-primary sm:text-sm h-[42px] px-3" type="text" value={modalUser.name || ''} onChange={e => setModalUser(m => ({ ...m, name: e.target.value }))} />
                        </div>
                        <div className="space-y-1.5">
                          <label className="text-sm font-medium text-gray-700 dark:text-slate-300">E-posta Adresi</label>
                          <input className="block w-full rounded-lg border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-gray-900 dark:text-white shadow-sm focus:border-primary focus:ring-primary sm:text-sm h-[42px] px-3" type="email" value={modalUser.email || ''} onChange={e => setModalUser(m => ({ ...m, email: e.target.value }))} />
                        </div>
                        <div className="space-y-1.5">
                          <label className="text-sm font-medium text-gray-700 dark:text-slate-300">Telefon</label>
                          <input className="block w-full rounded-lg border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-gray-900 dark:text-white shadow-sm focus:border-primary focus:ring-primary sm:text-sm h-[42px] px-3" type="text" value={modalUser.phone || ''} onChange={e => setModalUser(m => ({ ...m, phone: e.target.value }))} />
                        </div>
                        <button
                          type="button"
                          onClick={() => setActiveTab('abonelikler')}
                          className="w-full rounded-2xl border border-primary/20 bg-primary/5 dark:bg-primary/10 px-4 py-4 text-left transition hover:border-primary/40 hover:bg-primary/10"
                        >
                          <div className="flex items-center justify-between gap-3">
                            <div>
                              <div className="flex items-center gap-2 text-sm font-bold text-primary">
                                <span className="material-symbols-outlined text-[18px]">workspace_premium</span>
                                Abonelik Yönetimi
                              </div>
                              <p className="mt-1 text-xs text-gray-600 dark:text-slate-400">
                                EFT ile süre tanımla, bitişi takip et, son 7 gün uyarısını bu alandan yönet.
                              </p>
                            </div>
                            <span className="material-symbols-outlined text-primary">arrow_forward</span>
                          </div>
                        </button>
                      </div>
                      <div className="space-y-4">
                        <h5 className="text-sm font-semibold text-gray-900 dark:text-white border-b dark:border-slate-700 pb-2 mb-2">Sistem Bilgileri</h5>
                        <div className="space-y-1.5">
                          <label className="text-sm font-medium text-gray-700 dark:text-slate-300">Kullanıcı Tipi</label>
                          <select
                            className="block w-full rounded-lg border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-gray-900 dark:text-white shadow-sm focus:border-primary focus:ring-primary sm:text-sm h-[42px] px-3 font-medium"
                            value={modalUser.isAdmin ? 'Admin' : ((selectedUser.level || 0) > 0 ? 'Partner Üye' : 'Normal Üye')}
                            onChange={e => {
                              const val = e.target.value;
                              setModalUser(m => ({
                                ...m,
                                isAdmin: val === 'Admin'
                              }));
                              if (val !== 'Admin') {
                                setSelectedRbacRoleKeys([]);
                              } else if (selectedRbacRoleKeys.length === 0) {
                                setSelectedRbacRoleKeys([ADMIN_ROLE_KEYS.SUPER_ADMIN]);
                              }
                            }}
                          >
                            <option value="Normal Üye">Normal Üye</option>
                            <option value="Partner Üye" disabled>Partner Üye (Otomatik)</option>
                            <option value="Admin">Sistem Admini</option>
                          </select>
                        </div>
                        <div className="space-y-1.5">
                          <label className="text-sm font-medium text-gray-700 dark:text-slate-300">Rol(ler)</label>
                          <div className={`rounded-lg border px-3 py-2 ${modalUser.isAdmin ? 'border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900' : 'border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800'}`}>
                            {availableRbacRoles.length === 0 ? (
                              <p className="text-xs text-gray-500 dark:text-slate-400">Rol listesi bulunamadı.</p>
                            ) : (
                              <div className="grid grid-cols-1 gap-2">
                                {availableRbacRoles.map((role) => {
                                  const roleKey = String(role.roleKey || '').trim().toLowerCase();
                                  const checked = selectedRbacRoleKeys.includes(roleKey);
                                  return (
                                    <label key={`rbac-role-${roleKey}`} className={`flex items-center gap-2 text-sm ${modalUser.isAdmin ? 'text-gray-800 dark:text-slate-200' : 'text-gray-400 dark:text-slate-500'}`}>
                                      <input
                                        type="checkbox"
                                        className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary disabled:opacity-50"
                                        checked={checked}
                                        disabled={!modalUser.isAdmin}
                                        onChange={(e) => {
                                          const isChecked = e.target.checked;
                                          setSelectedRbacRoleKeys((prev) => {
                                            const current = Array.isArray(prev) ? prev : [];
                                            if (isChecked) return Array.from(new Set([...current, roleKey]));
                                            return current.filter((x) => x !== roleKey);
                                          });
                                        }}
                                      />
                                      <span>{role.roleName || roleKey}</span>
                                      <span className="text-xs text-gray-500 dark:text-slate-400">({roleKey})</span>
                                    </label>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 dark:text-slate-400">
                            Not: Admin kullanıcı için en az bir rol seçilmelidir.
                          </p>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-1.5">
                            <label className="text-sm font-medium text-gray-700 dark:text-slate-300">Kayıt Tarihi</label>
                            <input className="block w-full rounded-lg border-gray-300 dark:border-slate-600 bg-gray-50 dark:bg-slate-800 text-gray-500 dark:text-slate-400 shadow-sm focus:border-primary focus:ring-primary sm:text-sm h-[42px] px-3" readOnly type="text" defaultValue={selectedUser ? new Date(selectedUser.createdAt).toLocaleDateString('tr-TR') : ''} />
                          </div>
                          <div className="space-y-1.5">
                            <label className="text-sm font-medium text-gray-700 dark:text-slate-300">Son Giriş</label>
                            <input className="block w-full rounded-lg border-gray-300 dark:border-slate-600 bg-gray-50 dark:bg-slate-800 text-gray-500 dark:text-slate-400 shadow-sm focus:border-primary focus:ring-primary sm:text-sm h-[42px] px-3" readOnly type="text" defaultValue={selectedUser && selectedUser.lastActiveAt ? formatDateTime(selectedUser.lastActiveAt) : '-'} />
                          </div>
                        </div>
                        <div className="space-y-3 mt-4">
                          <div className="rounded-2xl border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-900/50 px-4 py-4">
                            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                              <div>
                                <h6 className="text-xs font-bold text-gray-800 dark:text-gray-300 uppercase tracking-tight mb-2">Premium Yönetimi</h6>
                                <p className="text-sm text-gray-600 dark:text-slate-400">
                                  Premium erisim artik bu alandan degil, sadece <span className="font-semibold text-primary">Abonelikler</span> sekmesinden yonetilir.
                                </p>
                                <p className="mt-2 text-xs text-gray-500 dark:text-slate-500">
                                  EFT ile sure tanimlama, bitis tarihi, kalan gun ve iptal islemleri yeni abonelik yapisina tasindi.
                                </p>
                              </div>
                              <button
                                type="button"
                                onClick={() => setActiveTab('abonelikler')}
                                className="inline-flex items-center justify-center gap-2 rounded-xl border border-primary/20 bg-primary/5 px-4 py-2.5 text-sm font-semibold text-primary hover:bg-primary/10"
                              >
                                <span className="material-symbols-outlined text-[18px]">workspace_premium</span>
                                Abonelikler'e Git
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {activeTab === 'abonelikler' && (
                    <div className="space-y-6">
                      <div className="grid grid-cols-1 xl:grid-cols-[1.15fr_0.85fr] gap-6">
                        <div className="rounded-2xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900/40 overflow-hidden">
                          <div className="px-5 py-4 border-b border-gray-100 dark:border-slate-700 flex items-center justify-between">
                            <div>
                              <h5 className="text-sm font-semibold text-gray-900 dark:text-white">Aktif ve Gecmis Abonelikler</h5>
                              <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">Suresi, odeme tipi ve kalan gun bilgisini buradan yonetebilirsin.</p>
                            </div>
                            {subscriptionLoading && (
                              <span className="text-xs text-gray-500 dark:text-slate-400">Yukleniyor...</span>
                            )}
                          </div>

                          <div className="p-5 space-y-3">
                            {subscriptionData.entitlements?.lockedByTime && (
                              <div className="rounded-xl border border-amber-200 bg-amber-50 dark:border-amber-900/40 dark:bg-amber-900/10 px-4 py-3 text-sm text-amber-800 dark:text-amber-300">
                                7 gunluk kilit aktif. Bu kullanici icin premium mutfaklar {subscriptionData.entitlements.daysRemaining} gun sonra otomatik acilir.
                              </div>
                            )}

                            {!subscriptionLoading && subscriptionData.grants.length === 0 && (
                              <div className="rounded-xl border border-dashed border-gray-300 dark:border-slate-600 px-4 py-8 text-center text-sm text-gray-500 dark:text-slate-400">
                                Bu kullanici icin henuz tanimli bir abonelik yok.
                              </div>
                            )}

                            {subscriptionData.grants.map((grant) => {
                              const remaining = daysRemaining(grant.expiresAt);
                              const isActive = grant.status === 'active' && (remaining === null || remaining >= 0);
                              const moduleLabels = {
                                global: 'Global Premium',
                                alakart: 'Alakart',
                                allergen: 'Alerjen',
                                diet: 'Diyet',
                                athlete: 'Sporcu',
                                special_days: 'Ozel Gunler'
                              };
                              const billingLabels = {
                                eft: 'EFT',
                                manual: 'Manuel',
                                complimentary: 'Hediye'
                              };

                              return (
                                <div key={grant.id} className="rounded-2xl border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-900/60 px-4 py-4">
                                  <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                                    <div className="space-y-2">
                                      <div className="flex flex-wrap items-center gap-2">
                                        <span className="text-sm font-semibold text-gray-900 dark:text-white">{moduleLabels[grant.moduleKey] || grant.moduleKey}</span>
                                        <span className={`px-2.5 py-1 rounded-full text-[11px] font-semibold ${isActive ? 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-300' : 'bg-gray-200 text-gray-700 dark:bg-slate-700 dark:text-slate-300'}`}>
                                          {grant.status}
                                        </span>
                                        <span className="px-2.5 py-1 rounded-full text-[11px] font-semibold bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300">
                                          {billingLabels[grant.billingType] || grant.billingType}
                                        </span>
                                      </div>
                                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-gray-600 dark:text-slate-400">
                                        <div>Baslangic: <span className="font-medium text-gray-800 dark:text-slate-200">{formatDateTime(grant.startedAt)}</span></div>
                                        <div>Bitis: <span className="font-medium text-gray-800 dark:text-slate-200">{formatDateTime(grant.expiresAt)}</span></div>
                                        <div>Kalan gun: <span className={`font-semibold ${remaining !== null && remaining <= 7 ? 'text-red-500' : 'text-gray-800 dark:text-slate-200'}`}>{remaining ?? '-'}</span></div>
                                        <div>Referans: <span className="font-medium text-gray-800 dark:text-slate-200">{grant.paymentReference || '-'}</span></div>
                                      </div>
                                      {grant.note && (
                                        <div className="text-xs text-gray-600 dark:text-slate-400 rounded-lg bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 px-3 py-2">
                                          {grant.note}
                                        </div>
                                      )}
                                    </div>

                                    {grant.status === 'active' && (
                                      <button
                                        onClick={() => handleCancelSubscription(grant.id)}
                                        disabled={subscriptionActionLoading}
                                        className="inline-flex items-center justify-center gap-2 rounded-xl border border-red-200 dark:border-red-900/30 bg-red-50 dark:bg-red-900/10 px-4 py-2 text-sm font-medium text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/20 disabled:opacity-60"
                                      >
                                        <span className="material-symbols-outlined text-[18px]">close</span>
                                        Iptal Et
                                      </button>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>

                        <div className="rounded-2xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900/40 overflow-hidden">
                          <div className="px-5 py-4 border-b border-gray-100 dark:border-slate-700">
                            <h5 className="text-sm font-semibold text-gray-900 dark:text-white">Yeni Abonelik Tanimla</h5>
                            <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">EFT veya manuel odemeyi gir, sureyi sec, sistem erisimi otomatik yonetsin.</p>
                          </div>

                          <div className="p-5 space-y-4">
                            <div className="space-y-1.5">
                              <label className="text-sm font-medium text-gray-700 dark:text-slate-300">Modul</label>
                              <select
                                className="block w-full rounded-lg border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-gray-900 dark:text-white shadow-sm focus:border-primary focus:ring-primary sm:text-sm h-[42px] px-3"
                                value={subscriptionForm.moduleKey}
                                onChange={(e) => setSubscriptionForm((prev) => ({ ...prev, moduleKey: e.target.value }))}
                              >
                                <option value="global">Global Premium</option>
                                <option value="alakart">Alakart</option>
                                <option value="allergen">Alerjen</option>
                                <option value="diet">Diyet</option>
                                <option value="athlete">Sporcu</option>
                                <option value="special_days">Ozel Gunler</option>
                              </select>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div className="space-y-1.5">
                                <label className="text-sm font-medium text-gray-700 dark:text-slate-300">Sure</label>
                                <select
                                  className="block w-full rounded-lg border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-gray-900 dark:text-white shadow-sm focus:border-primary focus:ring-primary sm:text-sm h-[42px] px-3"
                                  value={subscriptionForm.durationPreset}
                                  onChange={(e) => setSubscriptionForm((prev) => ({ ...prev, durationPreset: e.target.value }))}
                                >
                                  <option value="1">1 Ay</option>
                                  <option value="3">3 Ay</option>
                                  <option value="6">6 Ay</option>
                                  <option value="12">12 Ay</option>
                                  <option value="custom">Ozel Tarih</option>
                                </select>
                              </div>
                              <div className="space-y-1.5">
                                <label className="text-sm font-medium text-gray-700 dark:text-slate-300">Odeme Tipi</label>
                                <select
                                  className="block w-full rounded-lg border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-gray-900 dark:text-white shadow-sm focus:border-primary focus:ring-primary sm:text-sm h-[42px] px-3"
                                  value={subscriptionForm.billingType}
                                  onChange={(e) => setSubscriptionForm((prev) => ({ ...prev, billingType: e.target.value }))}
                                >
                                  <option value="eft">EFT</option>
                                  <option value="manual">Manuel</option>
                                  <option value="complimentary">Hediye</option>
                                </select>
                              </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div className="space-y-1.5">
                                <label className="text-sm font-medium text-gray-700 dark:text-slate-300">Baslangic</label>
                                <input
                                  type="datetime-local"
                                  className="block w-full rounded-lg border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-gray-900 dark:text-white shadow-sm focus:border-primary focus:ring-primary sm:text-sm h-[42px] px-3"
                                  value={subscriptionForm.startedAt}
                                  onChange={(e) => setSubscriptionForm((prev) => ({ ...prev, startedAt: e.target.value }))}
                                />
                              </div>
                              <div className="space-y-1.5">
                                <label className="text-sm font-medium text-gray-700 dark:text-slate-300">Bitis</label>
                                <input
                                  type="datetime-local"
                                  disabled={subscriptionForm.durationPreset !== 'custom'}
                                  className="block w-full rounded-lg border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-gray-900 dark:text-white shadow-sm focus:border-primary focus:ring-primary sm:text-sm h-[42px] px-3 disabled:opacity-60"
                                  value={subscriptionForm.durationPreset === 'custom'
                                    ? subscriptionForm.expiresAt
                                    : toDateTimeLocalInput(addMonthsToDate(subscriptionForm.startedAt || new Date(), Number(subscriptionForm.durationPreset)))}
                                  onChange={(e) => setSubscriptionForm((prev) => ({ ...prev, expiresAt: e.target.value }))}
                                />
                              </div>
                            </div>

                            <div className="space-y-1.5">
                              <label className="text-sm font-medium text-gray-700 dark:text-slate-300">Odeme Referansi</label>
                              <input
                                type="text"
                                className="block w-full rounded-lg border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-gray-900 dark:text-white shadow-sm focus:border-primary focus:ring-primary sm:text-sm h-[42px] px-3"
                                placeholder="EFT dekont no / aciklama"
                                value={subscriptionForm.paymentReference}
                                onChange={(e) => setSubscriptionForm((prev) => ({ ...prev, paymentReference: e.target.value }))}
                              />
                            </div>

                            <div className="space-y-1.5">
                              <label className="text-sm font-medium text-gray-700 dark:text-slate-300">Not</label>
                              <textarea
                                rows={3}
                                className="block w-full rounded-lg border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-gray-900 dark:text-white shadow-sm focus:border-primary focus:ring-primary sm:text-sm px-3 py-2"
                                placeholder="Orn: EFT teyit edildi, 3 aylik paket verildi."
                                value={subscriptionForm.note}
                                onChange={(e) => setSubscriptionForm((prev) => ({ ...prev, note: e.target.value }))}
                              />
                            </div>

                            {subscriptionError && (
                              <div className="rounded-xl border border-red-200 bg-red-50 dark:border-red-900/30 dark:bg-red-900/10 px-4 py-3 text-sm text-red-700 dark:text-red-300">
                                {subscriptionError}
                              </div>
                            )}

                            {subscriptionSuccess && (
                              <div className="rounded-xl border border-green-200 bg-green-50 dark:border-green-900/30 dark:bg-green-900/10 px-4 py-3 text-sm text-green-700 dark:text-green-300">
                                {subscriptionSuccess}
                              </div>
                            )}

                            <button
                              onClick={handleCreateSubscription}
                              disabled={subscriptionActionLoading}
                              className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-3 text-sm font-semibold text-white hover:bg-primary-hover disabled:opacity-60"
                            >
                              <span className="material-symbols-outlined text-[18px]">workspace_premium</span>
                              {subscriptionActionLoading ? 'Kaydediliyor...' : 'Aboneligi Aktiflestir'}
                            </button>
                          </div>
                        </div>
                      </div>

                      {subscriptionData.entitlements?.modules && (
                        <div className="rounded-2xl border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-900/40 px-5 py-4">
                          <h6 className="text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-slate-400 mb-3">Anlik Erisim Ozetleri</h6>
                          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                            {Object.entries(subscriptionData.entitlements.modules).map(([key, item]) => (
                              <div key={key} className="rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 py-3">
                                <div className="flex items-center justify-between gap-3">
                                  <span className="text-sm font-medium text-gray-900 dark:text-white">{key}</span>
                                  <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${item.granted ? 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-300' : 'bg-gray-200 text-gray-700 dark:bg-slate-700 dark:text-slate-300'}`}>
                                    {item.granted ? 'acik' : 'kapali'}
                                  </span>
                                </div>
                                <div className="mt-2 text-xs text-gray-500 dark:text-slate-400">
                                  Kaynak: <span className="font-medium text-gray-700 dark:text-slate-200">{item.source}</span>
                                </div>
                                <div className="mt-1 text-xs text-gray-500 dark:text-slate-400">
                                  Bitis: <span className="font-medium text-gray-700 dark:text-slate-200">{formatDateTime(item.expiresAt)}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Profil Bilgileri Tab */}
                  {activeTab === 'profil' && (
                    <div>
                      <h5 className="text-sm font-semibold text-gray-900 dark:text-white border-b dark:border-slate-700 pb-2 mb-4">Profil Bilgileri</h5>
                      {modalAnswers.profile ? (
                        <div className="grid grid-cols-1 gap-3">
                          {/* Partner Bilgisi */}
                          {(selectedUser.partnerId || (users || []).find(x => x.partnerId === selectedUser.id)) && (
                            <div className="flex items-center justify-between p-3 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg border border-indigo-200 dark:border-indigo-800/50 mb-2">
                              <div className="flex items-center gap-3">
                                {modalAnswers.profile?.partnerAvatar ? (
                                  <img src={modalAnswers.profile.partnerAvatar} alt="Partner" className="w-10 h-10 rounded-full object-cover border-2 border-white dark:border-slate-800" />
                                ) : (
                                  <div className="w-10 h-10 rounded-full bg-white dark:bg-slate-800 flex items-center justify-center border-2 border-indigo-100 dark:border-indigo-900/30">
                                    <span className="material-symbols-outlined text-indigo-500">handshake</span>
                                  </div>
                                )}
                                <div>
                                  <div className="text-sm text-indigo-700 dark:text-indigo-300 font-bold">Partner Bağlantısı</div>
                                  <div className="text-xs text-indigo-600 dark:text-indigo-400 font-medium">
                                    {selectedUser.partnerId
                                      ? `${(users || []).find(x => x.id === selectedUser.partnerId)?.name || 'Bilinmiyor'} (Parent)`
                                      : `${(users || []).find(x => x.partnerId === selectedUser.id)?.name || 'Bilinmiyor'} (Child)`
                                    }
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}

                          {Object.entries(modalAnswers.profile).filter(([k]) => !['intakeSurvey', 'id', 'children'].includes(k)).map(([k, v]) => {
                            if (v === null || v === undefined) return null;

                            const labelMap = {
                              locale: 'Dil',
                              hasBaby: 'Bebeği Var',
                              babyAgeGroup: 'Bebek Yaş Grubu',
                              babyBirthDate: 'Doğum Tarihi',
                              babyName: 'Bebek İsmi',
                              babyModalCompleted: 'Bebek Modal Tamamlandı',
                              lastModalShown: 'Son Modal Tarihi',
                              servingSize: 'Varsayılan Porsiyon Büyüklüğü',
                              dietaryRestrictions: 'Diyet Kısıtlamaları'
                            };

                            const displayLabel = labelMap[k] || k;
                            let displayValue = String(v);

                            if (typeof v === 'boolean') {
                              displayValue = v ? 'Evet' : 'Hayır';
                            } else if (Array.isArray(v)) {
                              displayValue = v.length > 0 ? v.join(', ') : 'Boş';
                            } else if ((k === 'lastModalShown' || k === 'babyBirthDate') && v) {
                              displayValue = new Date(v).toLocaleDateString('tr-TR');
                            }

                            return (
                              <div key={k} className="flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800/50">
                                <div className="text-sm text-gray-700 dark:text-slate-300 font-medium">{displayLabel}</div>
                                <div className="text-sm text-gray-800 dark:text-blue-200 font-medium">{displayValue}</div>
                              </div>
                            );
                          }).filter(Boolean)}
                        </div>
                      ) : (
                        <div className="text-center py-8">
                          <div className="text-gray-400 dark:text-slate-600 mb-3">
                            <span className="material-symbols-outlined text-[48px]">account_circle</span>
                          </div>
                          <p className="text-gray-500 dark:text-slate-400 text-sm">Profil bilgileri bulunamadı</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Bebek Durumu Tab */}
                  {activeTab === "bebek" && (
                    <div className="space-y-6">
                      <h5 className="text-sm font-semibold text-gray-900 dark:text-white border-b dark:border-slate-700 pb-2 mb-4">Çocuklar ve Bebek Durumu</h5>

                      {/* Ana Bebek Verisi (Sistemdeki Temel Kayıt) */}
                      <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-xl border border-purple-100 dark:border-purple-800/50">
                        <h6 className="text-xs font-bold text-purple-800 dark:text-purple-400 uppercase tracking-tight mb-4 flex items-center gap-2">
                          <span className="material-symbols-outlined text-[18px]">child_care</span>
                          Ana Bebek Bilgisi (Beril Bebek)
                        </h6>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-1.5">
                            <label className="text-[11px] font-bold text-purple-700/70 dark:text-purple-400/70 uppercase">Bebek İsmi</label>
                            <input
                              className="block w-full rounded-lg border-purple-200 dark:border-purple-800 bg-white dark:bg-slate-900 text-gray-900 dark:text-white shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm h-[40px] px-3 font-medium"
                              type="text"
                              value={modalAnswers.babyName || (modalAnswers.profile && modalAnswers.profile.babyName) || ''}
                              onChange={e => setModalAnswers(prev => ({ ...prev, babyName: e.target.value }))}
                              placeholder="Bebek İsmi"
                            />
                          </div>
                          <div className="space-y-1.5">
                            <label className="text-[11px] font-bold text-purple-700/70 dark:text-purple-400/70 uppercase">Doğum Tarihi</label>
                            <input
                              className="block w-full rounded-lg border-purple-200 dark:border-purple-800 bg-white dark:bg-slate-900 text-gray-900 dark:text-white shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm h-[40px] px-3 font-medium"
                              type="date"
                              value={(() => {
                                const d = modalAnswers.babyBirthDate || (modalAnswers.profile && modalAnswers.profile.babyBirthDate);
                                if (!d) return '';
                                try {
                                  return new Date(d).toISOString().split('T')[0];
                                } catch (e) {
                                  return '';
                                }
                              })()}
                              onChange={e => setModalAnswers(prev => ({ ...prev, babyBirthDate: e.target.value }))}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Multiple Children Management Section */}
                      <div className="space-y-4 mb-8">
                        <div className="flex items-center justify-between">
                          <h6 className="text-xs font-bold text-gray-800 dark:text-gray-300 uppercase tracking-tight">Çocuk Bilgileri</h6>
                          <button
                            type="button"
                            onClick={() => setModalChildren([...modalChildren, { name: '', birthDate: '', addedAt: new Date().toISOString() }])}
                            className="text-[10px] bg-primary/10 text-primary hover:bg-primary hover:text-white px-2 py-1 rounded transition-colors flex items-center gap-1"
                          >
                            <span className="material-symbols-outlined text-[14px]">add</span>
                            Yeni Ekle
                          </button>
                        </div>

                        {modalChildren.length > 0 ? (
                          <div className="space-y-3">
                            {modalChildren.map((child, idx) => (
                              <div key={idx} className="bg-gray-50 dark:bg-slate-900/50 border border-gray-200 dark:border-slate-800 rounded-lg p-3 relative group">
                                <button
                                  type="button"
                                  onClick={() => setModalChildren(modalChildren.filter((_, i) => i !== idx))}
                                  className="absolute top-1 right-1 text-gray-400 hover:text-red-500 transition-colors"
                                >
                                  <span className="material-symbols-outlined text-[16px]">close</span>
                                </button>
                                <div className="flex items-center gap-3">
                                  <div className="relative group flex-shrink-0">
                                    {child.photo ? (
                                      <img src={child.photo} alt={child.name} className="w-12 h-12 rounded-lg object-cover border border-primary/20" />
                                    ) : (
                                      <div className="w-12 h-12 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center border border-slate-200 dark:border-slate-700 text-slate-400">
                                        <span className="material-symbols-outlined text-lg">child_care</span>
                                      </div>
                                    )}
                                    <label className="absolute -bottom-1 -right-1 w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center cursor-pointer hover:bg-red-700 transition-colors shadow-lg border border-white dark:border-slate-800 scale-0 group-hover:scale-100 transition-transform">
                                      <span className="material-symbols-outlined text-[12px]">photo_camera</span>
                                      <input
                                        type="file"
                                        accept="image/*"
                                        className="hidden"
                                        onChange={(e) => {
                                          const file = e.target.files[0];
                                          if (file) {
                                            const reader = new FileReader();
                                            reader.onload = (rev) => {
                                              const newChildren = [...modalChildren];
                                              newChildren[idx].photo = rev.target.result;
                                              setModalChildren(newChildren);
                                            };
                                            reader.readAsDataURL(file);
                                          }
                                        }}
                                      />
                                    </label>
                                  </div>
                                  <div className="flex-1 grid grid-cols-2 gap-3 mt-1">
                                    <div>
                                      <label className="block text-[10px] font-medium text-gray-500 dark:text-slate-500 mb-1">İsim</label>
                                      <input
                                        type="text"
                                        value={child.name || ''}
                                        onChange={(e) => {
                                          const newChildren = [...modalChildren];
                                          newChildren[idx].name = e.target.value;
                                          setModalChildren(newChildren);
                                        }}
                                        placeholder="Çocuk ismi"
                                        className="w-full rounded border-gray-300 dark:border-slate-700 dark:bg-slate-800 dark:text-white text-xs py-1 px-2"
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-[10px] font-medium text-gray-500 dark:text-slate-500 mb-1">Doğum Tarihi</label>
                                      <input
                                        type="date"
                                        value={child.birthDate ? child.birthDate.substring(0, 10) : ''}
                                        onChange={(e) => {
                                          const newChildren = [...modalChildren];
                                          newChildren[idx].birthDate = e.target.value;
                                          setModalChildren(newChildren);
                                        }}
                                        className="w-full rounded border-gray-300 dark:border-slate-700 dark:bg-slate-800 dark:text-white text-xs py-1 px-2"
                                      />
                                      {child.birthDate && (
                                        <p className="text-[9px] text-gray-500 mt-1">
                                          Yaş: {computeAgeLabel(child.birthDate)}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center py-4 bg-gray-50 dark:bg-slate-900/30 rounded border border-dashed border-gray-300 dark:border-slate-800 text-gray-500 text-xs">
                            Henüz çocuk eklenmemiş.
                          </div>
                        )}
                      </div>

                      <h5 className="text-xs font-bold text-gray-800 dark:text-gray-300 uppercase tracking-tight mb-4 pt-4 border-t dark:border-slate-800">Legacy Bebek Sistemi</h5>
                      {(() => {
                        // Get baby info from profile data
                        const profile = modalAnswers.profile || {};
                        const hasBaby = profile.hasBaby;
                        const babyName = profile.babyName;
                        const babyAgeGroup = profile.babyAgeGroup;
                        const babyBirthDate = profile.babyBirthDate;
                        const babyModalCompleted = profile.babyModalCompleted;
                        const lastModalShown = profile.lastModalShown;
                        let ageLabel = '';
                        if (babyBirthDate) {
                          ageLabel = computeAgeLabel(babyBirthDate);
                        }

                        if (!hasBaby) {
                          return (
                            <div className="text-center py-8">
                              <div className="text-gray-400 dark:text-slate-600 mb-3">
                                <span className="material-symbols-outlined text-[48px]">child_care</span>
                              </div>
                              <p className="text-gray-500 dark:text-slate-400 text-sm">Bu kullanıcının bebeği yoktur</p>
                            </div>
                          );
                        }

                        return (
                          <div className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800/50">
                                <div className="flex items-center gap-2 mb-2">
                                  <span className="material-symbols-outlined text-blue-600 dark:text-blue-400">child_care</span>
                                  <h6 className="font-medium text-blue-900 dark:text-blue-100">Bebeğim Var</h6>
                                </div>
                                <p className="text-blue-700 dark:text-blue-300 text-sm">✓ Aktif bebek profili</p>
                              </div>

                              {babyName && (
                                <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800/50">
                                  <div className="flex items-center gap-2 mb-2">
                                    <span className="material-symbols-outlined text-green-600 dark:text-green-400">badge</span>
                                    <h6 className="font-medium text-green-900 dark:text-green-100">Bebek İsmi</h6>
                                  </div>
                                  <p className="text-green-700 dark:text-green-300 font-medium">{babyName}</p>
                                </div>
                              )}
                            </div>

                            {(babyAgeGroup || babyBirthDate) && (
                              <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800/50">
                                <div className="flex items-center gap-2 mb-2">
                                  <span className="material-symbols-outlined text-purple-600 dark:text-purple-400">calendar_month</span>
                                  <h6 className="font-medium text-purple-900 dark:text-purple-100">Yaş / Doğum Tarihi</h6>
                                </div>
                                {babyBirthDate && (
                                  <p className="text-purple-700 dark:text-purple-300 font-medium">Doğum: {new Date(babyBirthDate).toLocaleDateString('tr-TR')}</p>
                                )}
                                {ageLabel && (
                                  <p className="text-purple-700 dark:text-purple-300 font-medium">Yaş: {ageLabel}</p>
                                )}
                                {babyAgeGroup && (
                                  <p className="text-purple-700 dark:text-purple-300 font-medium">Grup: {babyAgeGroup}</p>
                                )}
                              </div>
                            )}

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div className="p-3 bg-gray-50 dark:bg-slate-900/50 rounded-lg">
                                <div className="text-xs text-gray-500 dark:text-slate-400 mb-1">Modal Durumu</div>
                                <div className="flex items-center gap-2">
                                  <span className={`material-symbols-outlined text-[16px] ${babyModalCompleted ? 'text-green-600 dark:text-green-400' : 'text-yellow-600 dark:text-yellow-400'}`}>
                                    {babyModalCompleted ? 'check_circle' : 'pending'}
                                  </span>
                                  <span className="text-sm font-medium text-gray-900 dark:text-white">{babyModalCompleted ? 'Tamamlandı' : 'Bekliyor'}</span>
                                </div>
                              </div>

                              {lastModalShown && (
                                <div className="p-3 bg-gray-50 dark:bg-slate-900/50 rounded-lg">
                                  <div className="text-xs text-gray-500 dark:text-slate-400 mb-1">Son Modal</div>
                                  <div className="text-sm font-medium text-gray-900 dark:text-white">{new Date(lastModalShown).toLocaleDateString('tr-TR')}</div>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )}

                  {/* Anket Cevapları Tab */}
                  {activeTab === 'anket' && (
                    <div className="mt-6 max-w-2xl mx-auto">
                      <h5 className="text-sm font-semibold text-gray-900 dark:text-white border-b dark:border-slate-700 pb-2 mb-3">Anket Verileri</h5>
                        <RegistrationSurveyFields
                          answers={modalAnswers}
                          onChange={(nextAnswers) => setModalAnswers((prev) => ({ ...prev, ...nextAnswers }))}
                          density="compact"
                          tone="surface-dark"
                          className="rounded-[22px] bg-slate-50/40 dark:bg-zinc-900/20 border border-slate-100 dark:border-zinc-900/30 p-2.5 sm:p-3.5 shadow-sm"
                        />
                    </div>
                  )}

                  {/* Kiler Durumu Tab */}
                  {activeTab === 'kiler' && (
                    <div>
                      <h5 className="text-sm font-semibold text-gray-900 dark:text-white border-b dark:border-slate-700 pb-2 mb-3">Kullanıcının Malzeme Verileri</h5>

                      {/* Inventory Batches */}
                      <div className="mb-6">
                        <h6 className="text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">Envanter ({userIngredientData.batches.length})</h6>
                        {(() => {
                          // Fix Turkish character encoding issues - shared function
                          const fixTurkishChars = (str) => {
                            if (!str) return str;
                            return str
                              .replace(/so\?\?an/gi, 'soğan')
                              .replace(/s\?\?t/gi, 'süt')
                              .replace(/pirin\?\?/gi, 'pirinç')
                              .replace(/sar\?\?msak/gi, 'sarımsak')
                              .replace(/ye\?\?il/gi, 'yeşil')
                              .replace(/k\?\?rm\?\?z\?\?/gi, 'kırmızı')
                              .replace(/di\?\?/gi, 'diş')
                              // Additional fixes for recipe titles
                              .replace(/Dolmas\?\?/gi, 'Dolması')
                              .replace(/sarımsak\?\?\s+/gi, 'sarımsaklı ')
                              .replace(/sarımsakl\?\?/gi, 'sarımsaklı')
                              .replace(/A\?\?/gi, 'Aşı')
                              .replace(/A\?\?\?\?/gi, 'Aşı')
                              .replace(/Menemen/gi, 'Menemen')
                              .replace(/Pilav/gi, 'Pilav');
                          };

                          return userIngredientData.batches.length > 0 ? (
                            <div className="space-y-2">
                              {userIngredientData.batches.map((batch) => {
                                // Turkish translation for storage locations
                                const storageLocationMap = {
                                  'fridge': 'Buzdolabı',
                                  'freezer': 'Dondurucu',
                                  'pantry': 'Kiler',
                                  'cabinet': 'Dolap',
                                  'buzdolabı': 'Buzdolabı',
                                  'kiler': 'Kiler',
                                  'dondurucu': 'Dondurucu'
                                };

                                const displayLocation = storageLocationMap[batch.storage_location] || batch.storage_location;

                                return (
                                  <div key={batch.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-slate-900/50 rounded-lg border dark:border-slate-700">
                                    <div className="flex-1">
                                      <div className="font-medium text-gray-900 dark:text-white">{fixTurkishChars(batch.ingredient_name)}</div>
                                      <div className="text-sm text-gray-600 dark:text-slate-400">
                                        {batch.remaining_amount}/{batch.initial_amount} {batch.unit}
                                      </div>
                                      <div className="text-xs text-gray-500 dark:text-slate-500">
                                        Son Kullanma: {new Date(batch.expiry_date).toLocaleDateString('tr-TR')}
                                      </div>
                                    </div>
                                    <div className="flex flex-col gap-1">
                                      {batch.is_opened && <span className="px-2 py-1 text-xs bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400 rounded">Açık</span>}
                                      {batch.is_frozen && <span className="px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400 rounded">Dondurulmuş</span>}
                                      <span className="px-2 py-1 text-xs bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-slate-400 rounded text-center">{displayLocation}</span>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <div className="text-center py-6 text-gray-500 dark:text-slate-400 text-sm">
                              <span className="material-symbols-outlined text-[48px] text-gray-300 dark:text-slate-600 block mb-2">inventory_2</span>
                              Envanter verisi bulunmuyor
                            </div>
                          );
                        })()}
                      </div>

                      {/* Meal History */}
                      <div>
                        <h6 className="text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">Yemek Geçmişi ({userIngredientData.meals.length})</h6>
                        {(() => {
                          // Reuse the same Turkish char fix function - enhanced version
                          const fixTurkishChars = (str) => {
                            if (!str) return str;
                            return str
                              .replace(/so\?\?an/gi, 'soğan')
                              .replace(/s\?\?t/gi, 'süt')
                              .replace(/pirin\?\?/gi, 'pirinç')
                              .replace(/sar\?\?msak/gi, 'sarımsak')
                              .replace(/ye\?\?il/gi, 'yeşil')
                              .replace(/k\?\?rm\?\?z\?\?/gi, 'kırmızı')
                              .replace(/di\?\?/gi, 'diş')
                              // Additional fixes for recipe titles
                              .replace(/Dolmas\?\?/gi, 'Dolması')
                              .replace(/sar\?\?msakl\?\?\s*/gi, 'sarımsaklı ')
                              .replace(/sarımsakl\?\?\s*/gi, 'sarımsaklı ')
                              .replace(/A\?\?/gi, 'Aşı')
                              .replace(/A\?\?\?\?/gi, 'Aşı')
                              .replace(/Menemen/gi, 'Menemen')
                              .replace(/Pilav/gi, 'Pilav')
                              // Additional patterns from database
                              .replace(/Sar\?\?msakl\?\?\s*Pilav/gi, 'Sarımsaklı Pilav')
                              .replace(/S\?\?t\s*A\?\?\?\?/gi, 'Süt Aşı')
                              .replace(/\?\?\s*/g, 'ı')
                              .replace(/\?\s*/g, 'ı');
                          };

                          return userIngredientData.meals.length > 0 ? (
                            <div className="space-y-2">
                              {userIngredientData.meals.map((meal) => (
                                <div key={meal.id} className="p-3 bg-gray-50 dark:bg-slate-900/50 rounded-lg border dark:border-slate-700">
                                  <div className="flex items-center justify-between">
                                    <div className="font-medium text-gray-900 dark:text-white">{fixTurkishChars(meal.recipe_title)}</div>
                                    <div className="text-sm text-gray-500 dark:text-slate-500">
                                      {new Date(meal.cooked_at).toLocaleString('tr-TR')}
                                    </div>
                                  </div>
                                  {meal.used_ingredients && (() => {
                                    try {
                                      const ingredients = typeof meal.used_ingredients === 'string'
                                        ? JSON.parse(meal.used_ingredients)
                                        : meal.used_ingredients;
                                      return (
                                        <div className="mt-2">
                                          <details className="text-sm">
                                            <summary className="cursor-pointer text-blue-600 hover:text-blue-800">
                                              {Object.keys(ingredients || {}).length} malzeme kullanıldı
                                            </summary>
                                            <div className="mt-2 text-xs bg-white dark:bg-slate-900 p-2 rounded border dark:border-slate-700">
                                              <pre className="text-gray-600 dark:text-slate-400 overflow-x-auto">
                                                {JSON.stringify(
                                                  Object.fromEntries(
                                                    Object.entries(ingredients).map(([key, value]) => [
                                                      fixTurkishChars(key),
                                                      value
                                                    ])
                                                  ),
                                                  null,
                                                  2
                                                )}
                                              </pre>
                                            </div>
                                          </details>
                                        </div>
                                      );
                                    } catch (e) {
                                      return (
                                        <div className="mt-2 text-xs text-gray-500">
                                          Malzeme bilgisi formatı hatalı
                                        </div>
                                      );
                                    }
                                  })()}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-center py-6 text-gray-500 dark:text-slate-400 text-sm">
                              <span className="material-symbols-outlined text-[48px] text-gray-300 dark:text-slate-600 block mb-2">restaurant</span>
                              Yemek geçmişi bulunmuyor
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  )}
                </div>
                <div className="px-6 py-4 border-t border-gray-100 dark:border-slate-700 bg-gray-50 dark:bg-slate-900/50 rounded-b-2xl flex justify-end gap-3 sticky bottom-0">
                  <button onClick={() => closeModal()} className="px-5 py-2.5 bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-600 rounded-lg text-sm font-medium text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700 cursor-pointer shadow-sm transition-colors">İptal</button>
                  {activeTab !== 'abonelikler' && <button onClick={saveProfileChanges} className="px-5 py-2.5 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary-hover shadow-sm shadow-primary/30 transition-colors flex items-center gap-2"><span className="material-symbols-outlined text-[18px]">save</span> Değişiklikleri Kaydet</button>}
                </div>
                <div className="px-6 pb-4">
                  {saveSuccess ? <div className="text-sm text-green-600 dark:text-green-400">Kaydedildi</div> : null}
                  {saveError ? <div className="text-sm text-red-600 dark:text-red-400">Hata: {saveError}</div> : null}
                </div>

              </div>
            </div>
          )}

          {/* Silme Onay Modali */}
          {deleteModalOpen && (
            <div className="fixed inset-0 z-[200] flex items-center justify-center bg-gray-900/60 dark:bg-slate-900/80 backdrop-blur-sm p-4 animate-in fade-in duration-200">
              <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-md border dark:border-slate-700 overflow-hidden">
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-4 text-red-600 dark:text-red-400">
                    <span className="material-symbols-outlined text-[32px]">warning</span>
                    <h3 className="text-lg font-bold">Kullanıcı Silme Onayı</h3>
                  </div>

                  <p className="text-gray-600 dark:text-slate-400 text-sm mb-6">
                    {deleteType === 'single'
                      ? `${userToDelete?.name || userToDelete?.email} kullanıcısını silmek istediğinize emin misiniz?`
                      : `${selectedUserIds.length} kullanıcıyı toplu olarak silmek istediğinize emin misiniz?`
                    } bu işlem geri alınamaz.
                  </p>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">
                        İşlemi onaylamak için Yönetici Şifrenizi girin
                      </label>
                      <input
                        type="password"
                        className="block w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-gray-900 dark:text-white focus:ring-primary focus:border-primary sm:text-sm"
                        placeholder="Şifreniz"
                        value={deletePassword}
                        onChange={(e) => setDeletePassword(e.target.value)}
                        autoFocus
                      />
                      {deleteError && (
                        <p className="mt-1.5 text-xs text-red-600 dark:text-red-400">{deleteError}</p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="px-6 py-4 bg-gray-50 dark:bg-slate-900/50 flex justify-end gap-3">
                  <button
                    onClick={() => setDeleteModalOpen(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                  >
                    İptal
                  </button>
                  <button
                    onClick={handleDeleteConfirm}
                    disabled={deleteLoading || !deletePassword}
                    className="px-4 py-2 text-sm font-medium bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm shadow-red-600/20"
                  >
                    {deleteLoading ? (
                      <span className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <span className="material-symbols-outlined text-[18px]">delete_forever</span>
                    )}
                    Silmeyi Onayla
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </main >
    </div >
  );
}
